import * as JSZip from "jszip";

/**
 * Imágenes que lleva dentro un ZIP subido en una zona de intraorales. El ZIP se sigue subiendo como hasta ahora (o
 * descomprimido, según el subidor) y además se suben estas imágenes, como si el usuario las hubiera subido sueltas.
 *
 * Se descomprime en el navegador y nada se escribe en disco: de cada entrada solo se usa su nombre sin carpetas, así
 * que una ruta con «..» no puede llevar a ningún sitio (y aun así se descarta). Se ignoran carpetas, la basura de macOS
 * (`__MACOSX/`, `._foto.jpg`) y los ocultos, lo que no tenga extensión de imagen y lo que no lo sea por su contenido.
 * Contra un ZIP bomba: tope de entradas e imágenes, y de bytes descomprimidos de verdad (no los que declara el ZIP),
 * cortando la lectura en cuanto se pasa.
 */

/** Las que acepta la API para un intraoral (CreateArchivoAction) */
export const EXTENSIONES_IMAGEN_ZIP = ['jpg', 'jpeg', 'png', 'webp'];

/** Los modelos 3D de la zona de intraorales: de un ZIP descomprimido solo se suben estos y las imágenes */
export const EXTENSIONES_MODELO_3D = ['stl', 'ply', 'obj'];

export interface LimitesZipImagenes {
    /** Entradas del ZIP, de cualquier tipo y carpetas incluidas */
    maxEntradas: number;
    /** Imágenes que se llegan a subir */
    maxImagenes: number;
    /** Bytes descomprimidos de una imagen */
    maxBytesImagen: number;
    /** Bytes descomprimidos de todas las imágenes juntas */
    maxBytesTotal: number;
}

const MB = 1024 * 1024;

export const LIMITES_ZIP_IMAGENES: LimitesZipImagenes = {
    maxEntradas: 5000,
    maxImagenes: 200,
    maxBytesImagen: 50 * MB,
    maxBytesTotal: 500 * MB,
};

/** Un error con un mensaje que se puede enseñar tal cual al usuario */
export class ZipImagenesError extends Error {
}

/** Nombre sin carpetas */
export function nombreSinCarpetas(ruta: string): string {
    const partes = (ruta || '').split(/[\\/]/).filter(parte => parte !== '');
    return partes.length ? partes[partes.length - 1] : '';
}

/** Carpetas de macOS (`__MACOSX/`, `._foto.jpg`), ocultos y cualquier ruta con «..» */
export function esBasuraDeZip(ruta: string): boolean {
    const partes = (ruta || '').split(/[\\/]/).filter(parte => parte !== '');
    return partes.length === 0 || partes.some(parte => parte === '__MACOSX' || parte.startsWith('.'));
}

/** La extensión en minúsculas, o '' si no tiene */
function extensionDe(ruta: string): string {
    const nombre = nombreSinCarpetas(ruta);
    const punto = nombre.lastIndexOf('.');
    return punto > 0 ? nombre.substring(punto + 1).toLowerCase() : '';
}

export function tieneExtensionDeImagen(ruta: string): boolean {
    return EXTENSIONES_IMAGEN_ZIP.includes(extensionDe(ruta));
}

export function tieneExtensionDeModelo3D(ruta: string): boolean {
    return EXTENSIONES_MODELO_3D.includes(extensionDe(ruta));
}

/** El tipo de imagen por los primeros bytes del contenido, o null si no es JPG, PNG ni WEBP */
export function tipoDeImagen(bytes: Uint8Array): string | null {
    const empiezaPor = (firma: number[], desde = 0) => bytes.length >= desde + firma.length && firma.every((b, i) => bytes[desde + i] === b);
    if (empiezaPor([0xFF, 0xD8, 0xFF])) {
        return 'image/jpeg';
    }
    if (empiezaPor([0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A])) {
        return 'image/png';
    }
    // RIFF....WEBP
    if (empiezaPor([0x52, 0x49, 0x46, 0x46]) && empiezaPor([0x57, 0x45, 0x42, 0x50], 8)) {
        return 'image/webp';
    }
    return null;
}

/** El nombre con el que vino en el ZIP, antes de que JSZip le quite los «..» */
function rutaOriginal(entrada: JSZip.JSZipObject): string {
    return (entrada as any).unsafeOriginalName || entrada.name;
}

/**
 * Lee una entrada descomprimiéndola por trozos y la corta en cuanto pasa de `maxBytes`: el tamaño que declara el ZIP
 * puede ser mentira.
 */
function leerConTope(entrada: JSZip.JSZipObject, maxBytes: number): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
        const trozos: Uint8Array[] = [];
        let leidos = 0;
        let cortado = false;
        // internalStream existe en JSZip 3 aunque sus tipos no lo declaran
        const flujo = (entrada as any).internalStream('uint8array');
        flujo.on('data', (trozo: Uint8Array) => {
            if (cortado) {
                return;
            }
            leidos += trozo.length;
            if (leidos > maxBytes) {
                cortado = true;
                flujo.pause();
                reject(new ZipImagenesError('La imagen «' + nombreSinCarpetas(entrada.name) + '» del ZIP es demasiado grande: no se ha subido ninguna imagen del ZIP.'));
                return;
            }
            trozos.push(trozo);
        });
        flujo.on('error', (error: Error) => {
            if (!cortado) {
                cortado = true;
                reject(error);
            }
        });
        flujo.on('end', () => {
            if (cortado) {
                return;
            }
            const bytes = new Uint8Array(leidos);
            let posicion = 0;
            trozos.forEach(trozo => {
                bytes.set(trozo, posicion);
                posicion += trozo.length;
            });
            resolve(bytes);
        });
        flujo.resume();
    });
}

/**
 * Las imágenes de un ZIP, listas para subir con el nombre que tenían (sin carpetas). Un ZIP sin imágenes da una lista
 * vacía. Si el ZIP no se puede abrir o se pasa de algún tope, falla con un ZipImagenesError y no devuelve ninguna:
 * o todas, o ninguna, para que no se queden subidas a medias sin que se note.
 */
export async function extraerImagenesDeZip(zip: Blob, limites: LimitesZipImagenes = LIMITES_ZIP_IMAGENES): Promise<File[]> {
    let contenido: JSZip;
    try {
        contenido = await JSZip.loadAsync(zip);
    } catch (e) {
        throw new ZipImagenesError('No se ha podido abrir el ZIP para sacar sus imágenes.');
    }

    const entradas = Object.keys(contenido.files).map(clave => contenido.files[clave]);
    if (entradas.length > limites.maxEntradas) {
        throw new ZipImagenesError('El ZIP tiene demasiados archivos (' + entradas.length + '): no se ha subido ninguna imagen del ZIP.');
    }

    const candidatas = entradas.filter(entrada => !entrada.dir && !esBasuraDeZip(rutaOriginal(entrada))
        && tieneExtensionDeImagen(entrada.name));
    if (candidatas.length > limites.maxImagenes) {
        throw new ZipImagenesError('El ZIP tiene demasiadas imágenes (' + candidatas.length + ', como mucho ' + limites.maxImagenes
            + '): no se ha subido ninguna imagen del ZIP.');
    }

    const imagenes: File[] = [];
    let total = 0;
    // De una en una: así el tope total se aplica a lo que se ha descomprimido de verdad
    for (const entrada of candidatas) {
        const queda = limites.maxBytesTotal - total;
        let bytes: Uint8Array;
        try {
            bytes = await leerConTope(entrada, Math.min(limites.maxBytesImagen, queda));
        } catch (e) {
            if (e instanceof ZipImagenesError && queda < limites.maxBytesImagen) {
                throw new ZipImagenesError('Las imágenes del ZIP ocupan demasiado: no se ha subido ninguna imagen del ZIP.');
            }
            throw e instanceof ZipImagenesError ? e : new ZipImagenesError('No se ha podido leer el ZIP para sacar sus imágenes.');
        }
        total += bytes.length;
        const tipo = tipoDeImagen(bytes);
        if (!tipo) {
            // Con extensión de imagen pero sin serlo: se ignora, igual que cualquier otro archivo del ZIP
            continue;
        }
        imagenes.push(new File([bytes], nombreSinCarpetas(entrada.name), {type: tipo}));
    }
    return imagenes;
}
